import type { CreateSocialPostRequest } from '../../types/instagramAutomation.types';
import type {
    TiktokCommercialContent,
    TiktokCreatorInteractions,
    TiktokPrivacyLevel,
} from '../../types/tiktokDirectPost.types';
import { resolveTiktokPublishConsentType } from '../../utils/tiktokDirectPostUx';

export type YoutubePrivacy = 'public' | 'private' | 'unlisted';

export type { TiktokPrivacyLevel };

export interface YoutubePostFields {
    title: string;
    description: string;
    tags: string;
    privacy_status: YoutubePrivacy;
}

export interface TiktokPostFields {
    /** Empty string until user selects — required at publish */
    tiktok_privacy_level: TiktokPrivacyLevel | '';
    tiktok_allow_comment: boolean;
    tiktok_allow_duet: boolean;
    tiktok_allow_stitch: boolean;
    tiktok_commercial_content: TiktokCommercialContent;
    tiktok_publish_consent_accepted: boolean;
}

export function defaultYoutubeFields(projectTitle = '', sharedCaption = ''): YoutubePostFields {
    return {
        title: projectTitle.trim(),
        description: sharedCaption.trim(),
        tags: '',
        privacy_status: 'public',
    };
}

export function defaultTiktokFields(): TiktokPostFields {
    return {
        tiktok_privacy_level: '',
        tiktok_allow_comment: false,
        tiktok_allow_duet: false,
        tiktok_allow_stitch: false,
        tiktok_commercial_content: {
            disclosure_enabled: false,
            your_brand: false,
            branded_content: false,
        },
        tiktok_publish_consent_accepted: false,
    };
}

export function applyYoutubeToCreateBody(
    body: CreateSocialPostRequest,
    fields: YoutubePostFields,
): CreateSocialPostRequest {
    return {
        ...body,
        title: fields.title.trim(),
        description: fields.description.trim(),
        tags: fields.tags
            .split(',')
            .map(t => t.trim())
            .filter(Boolean),
        privacy_status: fields.privacy_status,
    };
}

export function applyTiktokToCreateBody(
    body: CreateSocialPostRequest,
    fields: TiktokPostFields,
    creatorInteractions?: TiktokCreatorInteractions | null,
): CreateSocialPostRequest {
    const consentType = resolveTiktokPublishConsentType(fields.tiktok_commercial_content);
    const result: CreateSocialPostRequest = {
        ...body,
        tiktok_publish_consent_accepted: fields.tiktok_publish_consent_accepted,
        tiktok_publish_consent_type: consentType,
    };
    if (fields.tiktok_privacy_level) {
        result.tiktok_privacy_level = fields.tiktok_privacy_level;
    }

    if (fields.tiktok_commercial_content.disclosure_enabled) {
        result.tiktok_commercial_content = fields.tiktok_commercial_content;
    }

    // Opt-in only: send allow_* when user checked ON. Omit false — backend rejects explicit
    // tiktok_allow_duet: false when duet is disabled on the creator account.
    if (fields.tiktok_allow_comment && !creatorInteractions?.comment_disabled) {
        result.tiktok_allow_comment = true;
    }
    if (fields.tiktok_allow_duet && !creatorInteractions?.duet_disabled) {
        result.tiktok_allow_duet = true;
    }
    if (fields.tiktok_allow_stitch && !creatorInteractions?.stitch_disabled) {
        result.tiktok_allow_stitch = true;
    }

    return result;
}
